import { ToastContext } from "context/ToastContext";
import {
  CONTRACT_TYPE_OPTIONS,
  modelRunIdempotencyKey,
} from "pages/PayerRules/contracts/helpers";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetContractFeeSchedulesQuery } from "store/queries/contractFeeSchedules";
import {
  DraftSiteTier,
  useCreateContractModelDraftRunMutation,
} from "store/queries/contractModelDrafts";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";

import {
  INewRunTypeChoice,
  buildRunName,
  buildStandaloneRunRequest,
  canCreateNewRun,
  existingContractLabel,
  existingContractType,
  needsSiteTier,
  pickExistingContract,
} from "./newRun";

const SITE_TIER_OPTIONS: { label: string; value: DraftSiteTier }[] = [
  { label: "Office / Non-Facility (POS 11)", value: "non_facility" },
  { label: "Facility / Outpatient (POS 22+)", value: "facility" },
  { label: "Both tiers (one combined schedule)", value: "both" },
];

const TYPE_CHOICES: {
  label: string;
  value: Exclude<INewRunTypeChoice, null>;
}[] = [
  ...(CONTRACT_TYPE_OPTIONS as {
    label: string;
    value: Exclude<INewRunTypeChoice, null | "unsure">;
  }[]),
  { label: "Not sure, let the AI decide", value: "unsure" },
];

/** Where a run opened from this page returns to. */
const RUN_LIST_PATH = "/contract-model-drafts";

interface INewContractModelDialogProps {
  visible: boolean;
  onHide: () => void;
}

/**
 * CLE-2811 — "New contract model". Provider, payer and the contract type are required; the rate tier
 * is asked only for a Professional contract. Once provider and payer are both chosen, an existing
 * contract for the pair is offered: modeling THAT ties the run to the contract (its attachments, and
 * the facts saved back onto it), exactly like "Model with AI" on the contract page.
 */
export function NewContractModelDialog({
  visible,
  onHide,
}: INewContractModelDialogProps) {
  const navigate = useNavigate();
  const toastRef = useContext(ToastContext);
  const [createRun, { isLoading: creating }] =
    useCreateContractModelDraftRunMutation();
  const { data: providersData } = useGetAllProvidersQuery();
  const { data: payersData } = useGetAllPayersQuery();

  const [providerId, setProviderId] = useState<string | null>(null);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [typeChoice, setTypeChoice] = useState<INewRunTypeChoice>(null);
  const [siteTier, setSiteTier] = useState<DraftSiteTier | null>(null);

  const providers = useMemo(
    () =>
      (providersData?.data ?? []) as {
        id: string;
        name: string;
        specialty: string;
      }[],
    [providersData]
  );
  const payers = useMemo(
    () => (payersData?.data ?? []) as { id: number; name: string }[],
    [payersData]
  );
  const providerOptions = useMemo(
    () =>
      providers
        .map((p) => ({ label: `${p.name} (${p.specialty})`, value: p.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providers]
  );
  const payerOptions = useMemo(
    () =>
      payers
        .map((p) => ({ label: p.name, value: p.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [payers]
  );

  const providerName = providers.find((p) => p.id === providerId)?.name;
  const payerName = payers.find((p) => p.id === payerId)?.name;
  const runName = buildRunName(providerName, payerName);

  // Looked up only once both are chosen: is there already a contract for this provider + payer?
  const { data: contracts, isFetching: lookingUp } =
    useGetContractFeeSchedulesQuery(
      { providerId: providerId ?? undefined, payerId: payerId ?? undefined },
      { skip: !visible || !providerId || payerId == null }
    );
  const existing =
    !lookingUp && providerId && payerId != null
      ? pickExistingContract(contracts, providerId, payerId)
      : null;
  const existingType = existing
    ? existingContractType(typeChoice, existing)
    : undefined;

  const form = { providerId, payerId, typeChoice, siteTier };

  const reset = () => {
    setProviderId(null);
    setPayerId(null);
    setTypeChoice(null);
    setSiteTier(null);
    onHide();
  };

  const openRun = (id: string) => {
    reset();
    navigate(`${RUN_LIST_PATH}/${id}`, { state: { from: RUN_LIST_PATH } });
  };

  const showError = (e: unknown) => {
    const apiMessage = (e as { data?: { message?: string } })?.data?.message;
    toastRef?.current?.show({
      severity: "error",
      summary: "Could not start the modeling run",
      detail:
        typeof apiMessage === "string" && apiMessage
          ? apiMessage
          : "Something went wrong. Please try again.",
      life: 6000,
    });
  };

  const handleCreate = async () => {
    if (creating || !canCreateNewRun(form)) return;
    try {
      const run = await createRun(
        buildStandaloneRunRequest(form, runName)
      ).unwrap();
      openRun(run.id);
    } catch (e) {
      showError(e);
    }
  };

  // Exactly what the contract page's "Model with AI" sends: provider, payer and documents all come
  // from the contract, and the stage-scoped key returns the facts run already in flight on a retry.
  const handleModelExisting = async () => {
    if (creating || !existing || existingType === undefined) return;
    try {
      const run = await createRun({
        contractFeeScheduleId: existing.id,
        contractType: existingType,
        idempotencyKey: modelRunIdempotencyKey(existing.id, "facts"),
        name: runName || undefined,
      }).unwrap();
      openRun(run.id);
    } catch (e) {
      showError(e);
    }
  };

  return (
    <Dialog
      header="New contract model"
      visible={visible}
      style={{ width: "36rem", maxWidth: "95vw" }}
      onHide={reset}
      footer={
        <>
          <Button label="Cancel" className="p-button-text" onClick={reset} />
          <Button
            label="Create"
            icon="pi pi-check"
            disabled={creating || !canCreateNewRun(form)}
            loading={creating}
            onClick={handleCreate}
          />
        </>
      }
    >
      <label className="block mb-1 font-medium" htmlFor="run-provider">
        Provider
      </label>
      <Dropdown
        inputId="run-provider"
        className="w-full"
        value={providerId}
        options={providerOptions}
        filter
        placeholder="Select the provider entity"
        onChange={(e) => setProviderId(e.value)}
      />
      <small className="text-500 block mt-1 mb-3">
        The run&rsquo;s account is derived from this provider. Model one
        provider at a time &mdash; for an office/facility split, run it once per
        entity.
      </small>

      <label className="block mb-1 font-medium" htmlFor="run-payer">
        Payer
      </label>
      <Dropdown
        inputId="run-payer"
        className="w-full mb-3"
        value={payerId}
        options={payerOptions}
        filter
        placeholder="Select the payer"
        onChange={(e) => setPayerId(e.value)}
      />

      <span className="block mb-2 font-medium">Contract type</span>
      <div className="flex flex-column gap-2 mb-3">
        {TYPE_CHOICES.map((option) => (
          <div key={option.value} className="flex align-items-center gap-2">
            <RadioButton
              inputId={`run-type-${option.value}`}
              name="runContractType"
              checked={typeChoice === option.value}
              onChange={() => setTypeChoice(option.value)}
            />
            <label
              htmlFor={`run-type-${option.value}`}
              className="mb-0 cursor-pointer"
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>

      {needsSiteTier(typeChoice) && (
        <>
          <label className="block mb-1 font-medium" htmlFor="run-tier">
            Rate tier
          </label>
          <Dropdown
            inputId="run-tier"
            className="w-full"
            value={siteTier}
            options={SITE_TIER_OPTIONS}
            placeholder="Which rate column does this provider bill?"
            onChange={(e) => setSiteTier(e.value)}
          />
          <small className="text-500 block mt-1 mb-3">
            Contracts often list separate office and facility columns; this
            tells the model which one applies to this provider.
          </small>
        </>
      )}

      {existing && (
        <div className="p-3 mb-3 border-round surface-100 border-left-3 border-blue-500">
          <div className="flex gap-2">
            <i className="pi pi-info-circle text-blue-500 mt-1" />
            <div>
              <p className="m-0 mb-2">
                A contract already exists for this provider and payer:{" "}
                <strong>
                  {existingContractLabel(existing, runName || "this contract")}
                </strong>
                . Modeling it ties the run to the contract, uses its
                attachments, and saves the facts back onto it.
              </p>
              <Button
                label="Model that contract"
                icon="pi pi-sparkles"
                className="p-button-sm p-button-outlined"
                disabled={creating || existingType === undefined}
                onClick={handleModelExisting}
              />
              {existingType === undefined && (
                <small className="text-500 block mt-2">
                  Choose the contract type first &mdash; this contract does not
                  record one.
                </small>
              )}
            </div>
          </div>
        </div>
      )}

      <small className="text-500 block">
        The run starts by reading the contract facts; you&rsquo;ll upload
        documents on the next screen.
      </small>
    </Dialog>
  );
}
